import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { propertyTypeLabels, type PropertyInput, type PropertyType } from "../properties/api.js";
import { usStates } from "./address.js";

const propertyTypes: PropertyType[] = [
  "single_family",
  "multi_family",
  "condo",
  "townhouse",
  "commercial",
  "other",
];

export interface PropertyFormProps {
  initial?: Partial<PropertyInput>;
  submitLabel: string;
  onSubmit: (input: PropertyInput) => Promise<unknown>;
  /**
   * Where to navigate after a successful submit. Defaults to the properties
   * list. Callers (e.g. the RFQ-onboarding flow) can override this to bounce
   * the user back into the wizard they came from.
   */
  redirectTo?: string;
}

export function PropertyForm({
  initial,
  submitLabel,
  onSubmit,
  redirectTo = "/app/properties",
}: PropertyFormProps) {
  const navigate = useNavigate();
  const [nickname, setNickname] = useState(initial?.nickname ?? "");
  const [propertyType, setPropertyType] = useState<PropertyType>(
    initial?.propertyType ?? "single_family",
  );
  const [street, setStreet] = useState(initial?.street ?? "");
  const [unit, setUnit] = useState(initial?.unit ?? "");
  const [city, setCity] = useState(initial?.city ?? "");
  const [state, setState] = useState(initial?.state ?? "");
  const [postalCode, setPostalCode] = useState(initial?.postalCode ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedNickname = nickname.trim();
    const trimmedStreet = street.trim();
    const trimmedCity = city.trim();
    const trimmedPostal = postalCode.trim();
    const normalizedState = state.trim().toUpperCase();

    const nextFieldErrors: Record<string, string> = {};
    if (!trimmedNickname) {
      nextFieldErrors.nickname = "Nickname is required";
    } else if (trimmedNickname.length > 60) {
      nextFieldErrors.nickname = "Nickname must be 60 characters or fewer";
    }
    if (!trimmedStreet) nextFieldErrors.street = "Street is required";
    if (!trimmedCity) nextFieldErrors.city = "City is required";
    if (!normalizedState) {
      nextFieldErrors.state = "State is required";
    } else if (!usStates.some((s) => s.code === normalizedState)) {
      nextFieldErrors.state = "Select a valid US state";
    }
    // ZIP drives contractor matching, so require a clean 5-digit value here
    // (the API enforces the same). ZIP+4 is fine — we keep the first five.
    const zipDigits = trimmedPostal.replace(/\D/g, "");
    if (!trimmedPostal) {
      nextFieldErrors.postalCode = "ZIP is required";
    } else if (zipDigits.length < 5) {
      nextFieldErrors.postalCode = "Enter a valid 5-digit ZIP code";
    }

    setFieldErrors(nextFieldErrors);
    if (Object.keys(nextFieldErrors).length > 0) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        nickname: trimmedNickname,
        propertyType,
        street: trimmedStreet,
        unit: unit.trim() ? unit.trim() : undefined,
        city: trimmedCity,
        state: normalizedState,
        postalCode: zipDigits.slice(0, 5),
        country: "US",
        notes: notes.trim() ? notes.trim() : undefined,
      });
      navigate(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save property");
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4" noValidate>
      {error && <div className="rounded bg-rose-50 p-3 text-sm text-rose-900">{error}</div>}

      <Field label="Nickname" htmlFor="nickname" error={fieldErrors.nickname}>
        <input
          id="nickname"
          required
          autoComplete="off"
          maxLength={60}
          value={nickname}
          onChange={(e) => {
            setNickname(e.target.value);
            if (fieldErrors.nickname) {
              setFieldErrors((prev) => {
                const { nickname: _n, ...rest } = prev;
                return rest;
              });
            }
          }}
          aria-invalid={fieldErrors.nickname ? true : undefined}
          className={inputClass}
          placeholder="The Cottage"
        />
      </Field>

      <Field label="Type" htmlFor="propertyType">
        <select
          id="propertyType"
          value={propertyType}
          onChange={(e) => setPropertyType(e.target.value as PropertyType)}
          className={inputClass}
        >
          {propertyTypes.map((t) => (
            <option key={t} value={t}>
              {propertyTypeLabels[t]}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Street" htmlFor="street">
        <input
          id="street"
          required
          autoComplete="address-line1"
          value={street}
          onChange={(e) => setStreet(e.target.value)}
          className={inputClass}
        />
      </Field>

      <Field label="Unit / Apt (optional)" htmlFor="unit">
        <input
          id="unit"
          autoComplete="address-line2"
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          className={inputClass}
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="City" htmlFor="city">
          <input
            id="city"
            required
            autoComplete="address-level2"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="State" htmlFor="state" error={fieldErrors.state}>
          <select
            id="state"
            required
            autoComplete="address-level1"
            value={state}
            onChange={(e) => {
              setState(e.target.value);
              if (fieldErrors.state) {
                setFieldErrors((prev) => {
                  const { state: _s, ...rest } = prev;
                  return rest;
                });
              }
            }}
            aria-invalid={fieldErrors.state ? true : undefined}
            className={inputClass}
          >
            <option value="">Select…</option>
            {usStates.map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="ZIP" htmlFor="postalCode">
          <input
            id="postalCode"
            required
            autoComplete="postal-code"
            inputMode="numeric"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>

      <Field label="Notes (optional)" htmlFor="notes">
        <textarea
          id="notes"
          rows={3}
          maxLength={500}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={inputClass}
          placeholder="Gate code, parking, etc."
        />
      </Field>

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={() => navigate("/app/properties")}
          className="min-h-[44px] flex-1 rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="min-h-[44px] flex-1 rounded-md bg-teal-700 text-sm font-medium text-white disabled:opacity-60"
        >
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}

const inputClass =
  "block w-full min-h-[44px] rounded-md border border-slate-300 px-3 text-base focus:border-teal-700 focus:outline-none focus:ring-1 focus:ring-teal-700";

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
      {error && (
        <span className="mt-1 block text-sm text-rose-700" role="alert">
          {error}
        </span>
      )}
    </label>
  );
}
